import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ChecklistCompleteDialog from '@/app/pages/checklists/components/checklist-complete-dialog';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { DataTable } from '@/components/ui/datatable';
import { assuranceTasksColumnConfig } from '@/config/tableColumnConfig';
import { getTaskColumns } from './components/columns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Edit, Eye, Upload, Download, Search, ChevronDown, FileEdit, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import './task-register.css';
import { Task } from './mockTasks';
import PerformTaskDialog from '@/app/pages/compliance-calendar/PerformTaskDialog';
import EditTaskDialog from './EditTaskDialog';
import TaskHistoryDialog from './TaskHistoryDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { renderDate } from '../../../../shared/renderDate';
import { useGetUserFullName } from '@/utils/user-utils';
import { useTenancy } from '@/contexts/TenancyContext';
import { useCurrentUser } from '@/contexts/TenancyContext';
// Import the assurance actions service
import * as AssuranceService from '@/services/assuranceActionsService';
import { AssuranceTask } from '@/services/assuranceActionsService';
import { FirebaseTimestamp } from '@/js/external-js/config/main_config';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
// import { TourAlertDialog, useTour } from '@/components/tour';
// import { steps } from '@/config/tour';
import AddAssuranceTaskModal from './AddAssuranceTaskModal';
import { Checklist } from '../checklists/types/checklist.types';

// Utility to get task type variant for Badge component
const getDocTypeVariant = (type: string): string => {
  if (typeof type !== 'string') return 'other';
  const lowerType = type.toLowerCase();
  switch (lowerType) {
    case 'policy':
    case 'procedure':
    case 'form':
    case 'report':
    case 'record':
    case 'manual':
    case 'plan':
    case 'guideline':
    case 'standard':
    case 'checklist':
    case 'matrix':
    case 'presentation':
    case 'pdf':
    case 'evidence':
    case 'image':
    case 'spreadsheet':
    case 'database':
    case 'code':
    case 'website':
    case 'template':
    case 'hardware':
    case 'software':
    case 'service':
    case 'personnel':
    case 'taskation':
      return lowerType;
    default:
      return 'other';
  }
};

// Utility to get status variant for Badge component
const getStatusVariant = (status: string | boolean): string => {
  // Handle boolean values
  if (typeof status === 'boolean') {
    return status ? 'active' : 'inactive';
  }

  // Handle string values or convert to string if needed
  const statusStr = String(status);
  const lowerStatus = statusStr.toLowerCase();

  switch (lowerStatus) {
    case 'true':
    case 'current':
      return 'active';
    case 'false':
    case 'draft':
      return 'inactive';
    case 'expired':
      return 'decommissioned';
    default:
      return 'secondary';
  }
};

interface AssuranceLibraryProps {
  isReadOnly?: boolean;
  onBackNavigation?: () => void;
}

const AssuranceLibrary: React.FC<AssuranceLibraryProps> = ({
  isReadOnly = false,
  onBackNavigation
}) => {
  // const { setSteps } = useTour();
  // const [openTour, setOpenTour] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [historyTask, setHistoryTask] = useState<Task | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { activeTenancy } = useTenancy();
  const [searchTerm, setSearchTerm] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState<'all' | string>('all');
  const [showOverdueOnly, setShowOverdueOnly] = useState<boolean>(false);
  const [showDueToday, setShowDueToday] = useState<boolean>(false);
  const [ownerFilter, setOwnerFilter] = useState<'all' | string>('all');
  // Task dialog state
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPerformDialogOpen, setIsPerformDialogOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);
  const [hasHandledAssuranceParam, setHasHandledAssuranceParam] = useState(false);
  
  // Deleted tasks view state
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);
  const [isLongPressing, setIsLongPressing] = useState(false);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // State for tasks from Firestore
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const currentUser = useCurrentUser();

  // State for Add Assurance Task Modal
  const [addTaskModalOpen, setAddTaskModalOpen] = useState(false);

  // Checklist completion dialog submit handler
  const handleChecklistDialogSubmit = (values: Record<string, any>) => {
    // TODO: Implement Firestore update/submit logic
    console.log('Checklist completed (modal):', values);
    setChecklistDialogOpen(false);
    setChecklistDialogChecklist(null);
  };

  // State for Checklist completion dialog
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [checklistDialogChecklist, setChecklistDialogChecklist] = useState<Checklist | null>(null);

  // Helper to load tasks from Firestore and update state - DED-379 | The fix was removing the duplicate loadTasks function
  const loadTasks = async () => {
    try {
      setLoading(true);
      if (!activeTenancy.id || activeTenancy.id === '' || activeTenancy.id === 'Loading...') return;
      setError(null);

      // Main assurance tasks - include deleted tasks if in deleted view
      const assuranceTasks = await AssuranceService.getAllTasks(undefined, showDeletedTasks);
      const formattedTasks: Task[] = assuranceTasks.map(task => {
        const convertHistory = () => {
          if (!task.history) return {};
          const result: Record<string, any> = {};
          Object.keys(task.history).forEach((key) => {
            const entry = task.history![key];
            result[key] = {
              changeID: typeof entry.changeID === 'number' ? entry.changeID : parseInt(entry.changeID) || 0,
              changedBy: entry.changedBy || '',
              dateChanged:
                entry.dateChanged instanceof Date
                  ? { __time__: entry.dateChanged.toISOString() }
                  : (typeof entry.dateChanged === 'object' && entry.dateChanged?.__time__)
                    ? { __time__: entry.dateChanged.__time__ }
                    : (typeof entry.dateChanged === 'string')
                      ? { __time__: entry.dateChanged }
                      : (typeof entry.dateChanged === 'object' && entry.dateChanged.seconds && entry.dateChanged.nanoseconds)
                        ? entry.dateChanged
                        : { __time__: '' },
              fieldsChanged: entry.fieldsChanged || {}
            };
          });
          return result;
        };
        return {
          id: task.assID || '',
          assID: task.assID || '',
          componentId: 'SEC-' + Math.floor(Math.random() * 100),
          specialType: (task as any).specialType as 'meeting-agenda' | 'changes-3m' | 'changes-6m' | undefined,
          checkDetail: {
            active: task.checkDetail?.active || 'No Status',
            assignedTo: task.checkDetail?.assignedTo || 'No Owner',
            check: (task.checkDetail?.check || 'No Task').replace(/acme[\s,]*inc\.?/gi, activeTenancy.legalName),
            evidence: task.checkDetail?.evidence || [],
            framework: task.checkDetail?.framework || {},
            internalProcess: task.checkDetail?.control || '',
            meetingID: '',
            meetingNeeded: task.checkDetail?.meetingNeeded || 'false',
            procRef: [],
            procRef2013: []
          },
          checkFrequency: {
            checkDays: task.checkFrequency?.checkDays || '0',
            lastCheck: task.checkFrequency?.lastCheck || 'Not Set',
            nextCheck: task.checkFrequency?.nextCheck || 'Not Set',
            reminderActive: 'false',
            reminderDays: task.checkFrequency?.reminderDays || '0',
            sendReminder: task.checkFrequency?.sendReminder || 'false'
          },
          checkHistory: task.checkHistory || {
            active: 'true',
            numberPreviousChecks: '0'
          },
          history: convertHistory(),
          rawData: task,
          isChecklistTask: false
        };
      });

      // Checklist-based assurance tasks
      const { checklistService } = await import('@/app/pages/checklists/services/checklist-service');
      const checklists = await checklistService.getChecklists(activeTenancy.id);
      const checklistAssuranceTasks = checklists.filter(cl => cl.isAssuranceTask);
      const formattedChecklistTasks: Task[] = checklistAssuranceTasks.map(cl => ({
        id: cl.id,
        assID: cl.id,
        componentId: cl.id,
        specialType: undefined,
        checkDetail: {
          active: cl.status || 'No Status',
          assignedTo: cl.createdBy || 'No Owner',
          check: cl.title || 'No Task',
          evidence: [],
          framework: {},
          internalProcess: '',
          meetingID: '',
          meetingNeeded: 'false',
          procRef: [],
          procRef2013: []
        },
        checkFrequency: {
          checkDays: '',
          lastCheck: '',
          nextCheck: '',
          reminderActive: 'false',
          reminderDays: '',
          sendReminder: 'false'
        },
        checkHistory: {
          active: 'true',
          numberPreviousChecks: '0'
        },
        history: {},
        rawData: cl,
        isChecklistTask: true
      }));

      // Merge both lists for display
      setTasks([...formattedTasks, ...formattedChecklistTasks]);
      console.log('[Assurance Library] Formatted tasks:', [...formattedTasks, ...formattedChecklistTasks]);
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load assurance tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  // useEffect(() => {
  //   setSteps(steps);
  //   const timer = setTimeout(() => {
  //     setOpenTour(true);
  //   }, 100);

  //   return () => clearTimeout(timer);
  useEffect(() => {
    loadTasks();
  }, [activeTenancy.id, showDeletedTasks]);

  useEffect(() => {
    if (hasHandledAssuranceParam) return;
    const requestedAssuranceId = searchParams.get('assurance');
    if (!requestedAssuranceId) return;
    if (loading) return;

    const normalisedRequestedId = requestedAssuranceId.trim().toLowerCase();
    const matchingTask = tasks.find(task => {
      const candidates = [task.assID, task.id];
      return candidates.some(candidate => typeof candidate === 'string' && candidate.trim().toLowerCase() === normalisedRequestedId);
    });

    if (matchingTask && !matchingTask.isChecklistTask) {
      setTaskToEdit(matchingTask);
      setIsEditDialogOpen(true);
      setHasHandledAssuranceParam(true);
      return;
    }

    setSearchTerm(requestedAssuranceId);
    setHasHandledAssuranceParam(true);
  }, [searchParams, tasks, loading, hasHandledAssuranceParam]);

  // DED-1512: Normalize framework names to canonical forms to prevent duplicates
  // Maps variations like "E8", "Essential 8", "ESSENTIAL EIGHT" to a single canonical name
  const normalizeFrameworkName = (name: string): string => {
    const upperName = (name || 'Unknown').toString().toUpperCase().trim();
    
    // Essential Eight variations
    if (upperName === 'E8' || upperName === 'ESSENTIAL 8' || upperName === 'ESSENTIAL EIGHT' || 
        upperName.includes('E8 CONTROLS') || upperName.includes('E8CONTROLS') ||
        upperName.includes('ACSCE8')) {
      return 'ESSENTIAL EIGHT';
    }
    
    // ISO 27001 variations - normalize to version-specific names
    if (upperName.includes('ISO 27001') || upperName.includes('ISO27001')) {
      if (upperName.includes('2013')) {
        return 'ISO 27001:2013';
      }
      if (upperName.includes('2022')) {
        return 'ISO 27001:2022';
      }
      // Default to 2022 if no version specified
      return 'ISO 27001:2022';
    }
    
    // ISO 27701 variations
    if (upperName.includes('ISO 27701') || upperName.includes('ISO27701')) {
      return 'ISO 27701';
    }
    
    // ISO 42001 variations
    if (upperName.includes('ISO 42001') || upperName.includes('ISO42001')) {
      return 'ISO 42001';
    }
    
    // SOC 2 variations
    if (upperName === 'SOC2' || upperName === 'SOC 2' || upperName.includes('SOC2CONTROLS')) {
      return 'SOC 2';
    }
    
    // NIST variations
    if (upperName.includes('NIST 800-171') || upperName.includes('NIST800-171')) {
      return 'NIST 800-171';
    }
    if (upperName.includes('NIST CSF') || upperName.includes('NIST-CSF')) {
      return 'NIST CSF';
    }
    if (upperName.includes('NIST 800-53') || upperName.includes('NIST800-53')) {
      return 'NIST 800-53';
    }
    
    // SMB1001 variations
    if (upperName.includes('SMB1001') || upperName.includes('SMB 1001')) {
      return 'SMB 1001';
    }
    
    return upperName;
  };

  // Memoize filter options to prevent unnecessary re-renders
  const frameworkOptions = useMemo(() => {
    // Extract all framework names from all tasks
    const allFrameworks = tasks.flatMap(task => {
      const frameworks = task.checkDetail?.framework || {};
      return Object.values(frameworks).map(framework => {
        // Type assertion to handle the unknown type
        const typedFramework = framework as { frameName: string };
        // DED-1512: Normalize framework name to prevent duplicates
        return normalizeFrameworkName(typedFramework.frameName);
      });
    });
    // Create a unique set of framework names (duplicates are now normalized)
    return Array.from(new Set(allFrameworks)).sort();
  }, [tasks]);
  const statusOptions = useMemo(() => Array.from(new Set(tasks.map(a => a.checkDetail?.active || 'No Status'))), [tasks]);
  // Get user IDs for owner filter
  const ownerIds = useMemo(() => Array.from(new Set(tasks.map(a => a.checkDetail?.assignedTo || 'No Owner'))), [tasks]);

  // Use our custom hook to get the user name function
  const getUserFullName = useGetUserFullName();

  // Map user IDs to full names for the dropdown
  const ownerOptions = useMemo(() => {
    return ownerIds.map(userId => ({
      id: userId,
      name: getUserFullName(userId)
    }));
  }, [ownerIds, getUserFullName]);

  // Memoize filtered tasks to prevent unnecessary re-renders
  const filteredTasks = useMemo(() => {
    // If we're loading, return empty array
    if (loading) return [];

    return tasks.filter(a => {
      const check = a.checkDetail?.check || 'No Task';
      const active = a.checkDetail?.active || 'No Status';
      const assignedTo = a.checkDetail?.assignedTo || 'No Owner';

      const matchesSearch = check?.toLowerCase()?.includes(searchTerm.toLowerCase()) || false;
      // Check if any of the task's frameworks match the selected filter (case-insensitive)
      const frameworks = a.checkDetail?.framework || {};
      const frameNames = Object.values(frameworks).map(framework => {
        // Type assertion to handle the unknown type
        const typedFramework = framework as { frameName: string };
        // DED-1512: Use normalizeFrameworkName to match the normalized frameworkOptions
        return normalizeFrameworkName(typedFramework.frameName);
      });
      const matchesFramework = frameworkFilter === 'all' ? true : frameNames.includes(frameworkFilter);
      // Check if the task is overdue or due today
      const nextCheck = a.checkFrequency?.nextCheck;
      let reviewDate: Date | null = null;
      if (nextCheck && typeof nextCheck === 'object') {
        if (typeof nextCheck.toDate === 'function') {
          reviewDate = nextCheck.toDate();
        } else if (typeof nextCheck.seconds === 'number') {
          reviewDate = new Date(nextCheck.seconds * 1000);
        } else if (nextCheck.__time__) {
          reviewDate = new Date(nextCheck.__time__);
        }
      } else if (typeof nextCheck === 'string' && nextCheck !== 'Not Set') {
        reviewDate = new Date(nextCheck);
      }

      // Helper function to check if two dates are the same day
      const isSameDay = (date1: Date, date2: Date) => {
        return date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate();
      };

      // Get current date for comparison (midnight, ignore time)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let reviewDateLocal: Date | null = null;
      if (reviewDate instanceof Date && !isNaN(reviewDate.getTime())) {
        reviewDateLocal = new Date(reviewDate); // clone
        reviewDateLocal.setHours(0, 0, 0, 0);
      }

      const isDueToday = reviewDateLocal ? isSameDay(reviewDateLocal, today) : false;
      const isOverdue = reviewDateLocal ? reviewDateLocal < today : false;

      // Apply filters based on button states
      const matchesOverdueFilter = showOverdueOnly ? isOverdue : true;
      const matchesDueTodayFilter = showDueToday ? isDueToday : true;
      const matchesOwner = ownerFilter === 'all' ? true : assignedTo === ownerFilter;
      // If both filters are active, show tasks that match either condition
      // Note: Inactive tasks are shown but date filters only apply to active tasks
      const isActive = String(active).toLowerCase() === 'true' || active === true;
      
      // When date filters are active, only show active tasks that match the date criteria
      // When no date filters are active, show all tasks (both active and inactive)
      const matchesDateFilters = (showOverdueOnly || showDueToday) 
        ? (isActive && ((showOverdueOnly && isOverdue) || (showDueToday && isDueToday)))
        : true;

      return matchesSearch && matchesFramework && matchesDateFilters && matchesOwner;
    });
  }, [searchTerm, frameworkFilter, showOverdueOnly, showDueToday, ownerFilter, tasks, loading]);



  // Sorting logic
  const [sortKey, setSortKey] = useState<keyof Task>('checkDetail');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Sort tasks based on current sort key and order
  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      // Special handling for nested keys - we want to sort by task name
      const valueA = a.checkDetail?.check || '';
      const valueB = b.checkDetail?.check || '';

      // Handle string comparisons
      return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
  }, [filteredTasks, sortOrder]);

  const handleSort = (key: keyof Task) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  // Cleanup effect for long press timer
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };
  }, []);

  // Long press handlers for All Frameworks button
  const handleLongPressStart = () => {
    setIsLongPressing(true);
    longPressTimerRef.current = setTimeout(() => {
      setShowDeletedTasks(!showDeletedTasks);
      setIsLongPressing(false);
    }, 700); // 700ms threshold for better UX
  };

  const handleLongPressEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    // Reset visual feedback immediately
    setIsLongPressing(false);
  };

  const handleLongPressLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setIsLongPressing(false);
  };

  // Handle task restore
  const handleTaskRestore = async (taskId: string) => {
    try {
      const result = await AssuranceService.restoreTask(taskId);
      if (result?.success) {
        // Refresh tasks to update the list
        loadTasks();
      } else {
        setError('Failed to restore task. Please try again.');
      }
    } catch (err) {
      console.error('Error restoring task:', err);
      setError('Failed to restore task. Please try again.');
    }
  };

  // Handle task delete callback from EditTaskDialog
  const handleTaskDeleted = (taskId: string) => {
    // Remove the deleted task from local state
    setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId && task.assID !== taskId));
  };

  // Button style for active filter
  const getFilterButtonClass = (active: boolean) =>
    active
      ? 'filter-btn-active border font-semibold bg-primary-fade text-[color:var(--primary)] border-primary-fade-border dark:hover:text-[color:var(--primary)]'
      : 'border border-border bg-transparent text-foreground';

  const [helpContent, setHelpContent] = useState(null);
  useEffect(() => {
    getHelpContentByPage('assurance.tasks').then(setHelpContent);
  }, []);

  // The component's render JSX
  return (
    <div className="glass-card">
      <div className="assurance-library">
        <div>
          <h2 className="title flex flex-row">
            {showDeletedTasks ? (
              <>
                <span className="text-red-600 dark:text-red-400">Deleted Assurance Tasks</span>
                <span className="flex items-center bg-transparent pl-2 rounded font-semibold text-center">
                  <HelpPopoverHover config={helpContent?.['page.title']} />
                </span>
              </>
            ) : (
              <>
                Assurance Tasks
                <span className="flex items-center bg-transparent pl-2 rounded font-semibold text-center">
                  <HelpPopoverHover config={helpContent?.['page.title']} />
                </span>
              </>
            )}
          </h2>
          <p className="subtitle">
            {showDeletedTasks 
              ? 'View and restore previously deleted assurance tasks.'
              : 'This is your go-to space for completing and tracking important compliance activities.'
            }
          </p>
        </div>
        {/* Filters Row */}
        <div className="filters-row flex flex-col w-full gap-4 my-5">
          <div className="flex flex-col md:flex-row w-full gap-4">
            {/* Search Bar - 25% on desktop, full width on mobile */}
            <div className={`${isMobile ? 'w-full mb-3' : 'w-full md:w-1/3'}`}>
              <Input
                className="search-bar w-full"
                placeholder="Search Tasks"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                prefix={<Search size={16} />}
              />
            </div>

            {/* Filter Controls - 75% on desktop, full width on mobile */}
            <div className={`filter-controls flex-1 flex flex-wrap gap-2 justify-center md:justify-end`}>
              {/* All Tasks Button */}
              <Button
                variant="outline"
                size="sm"
                className={`${getFilterButtonClass(frameworkFilter === 'all' && !showOverdueOnly && !showDueToday && ownerFilter === 'all') + ' h-9 px-4 rounded-[var(--radius)] flex items-center gap-2'} ${isLongPressing ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/20 dark:border-red-700 dark:text-red-400' : ''}`}
                onClick={() => {
                  if (!isLongPressing) {
                    setFrameworkFilter('all');
                    setShowOverdueOnly(false);
                    setShowDueToday(false);
                    setOwnerFilter('all');
                  }
                }}
                onMouseDown={handleLongPressStart}
                onMouseUp={handleLongPressEnd}
                onMouseLeave={handleLongPressLeave}
                onTouchStart={handleLongPressStart}
                onTouchEnd={handleLongPressEnd}
                id={TOUR_STEP_IDS.TEAM_SWITCHER}
              >
                {isLongPressing ? (
                  <>Viewing Deleted...</>
                ) : showDeletedTasks ? (
                  <>Exit Deleted View</>
                ) : (
                  <>All Frameworks</>
                )}
              </Button>

              {/* Overdue Tasks Filter Button */}
              <Button
                variant="outline"
                size="sm"
                className={getFilterButtonClass(showOverdueOnly) + ' h-9 px-4 rounded-[var(--radius)] flex items-center gap-2'}
                onClick={() => {
                  setShowOverdueOnly(!showOverdueOnly);
                  // If turning on overdue filter, turn off due today filter
                  if (!showOverdueOnly) {
                    setShowDueToday(false);
                  }
                }}
              >
                Overdue Tasks
              </Button>

              {/* Due Today Filter Button */}
              <Button
                variant="outline"
                size="sm"
                className={getFilterButtonClass(showDueToday) + ' h-9 px-4 rounded-[var(--radius)] flex items-center gap-2'}
                onClick={() => {
                  setShowDueToday(!showDueToday);
                  // If turning on due today filter, turn off overdue filter
                  if (!showDueToday) {
                    setShowOverdueOnly(false);
                  }
                }}
              >
                Due Today
              </Button>

              {/* Framework Dropdown */}
              <Select value={frameworkFilter} onValueChange={v => setFrameworkFilter(v as typeof frameworkFilter)}>
                <SelectTrigger className={getFilterButtonClass(frameworkFilter !== 'all') + ' min-w-[120px] rounded-[var(--radius)] flex items-center gap-2'}>
                  <span>
                    {frameworkFilter !== 'all' ? `Framework: ${frameworkFilter}` : 'Framework'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Frameworks</SelectItem>
                  {frameworkOptions.map((framework) => (
                    <SelectItem key={framework} value={framework}>
                      {framework}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Owner Dropdown */}
              <Select value={ownerFilter} onValueChange={v => setOwnerFilter(v as typeof ownerFilter)}>
                <SelectTrigger className={getFilterButtonClass(ownerFilter !== 'all') + ' min-w-[120px] rounded-[var(--radius)] flex items-center gap-2'}>
                  <span>
                    {ownerFilter !== 'all' ? `Assigned To: ${getUserFullName(ownerFilter)}` : 'Assigned To'}
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignees</SelectItem>
                  {ownerOptions.map((owner) => (
                    <SelectItem key={owner.id} value={owner.id}>
                      {owner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Add Assurance Task Button - Only show in regular (non-readonly) mode and not in deleted view */}
              {!isReadOnly && !showDeletedTasks && (
                <Button
                  variant="default"
                  size="sm"
                  className="ml-2 h-9 px-4 rounded-[var(--radius)] flex items-center gap-2"
                  onClick={() => setAddTaskModalOpen(true)}
                >
                  <Plus className="mr-2 h-4 w-4" /> Add Assurance Task
                </Button>
              )}

            </div>
          </div>
        </div>

        {/* Task Dialogs and Task List wrapped in a React Fragment */}
        <>
          {/* Task Dialogs - Always render EditTaskDialog but with isReadOnly prop when in read-only mode */}
          <EditTaskDialog
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            selectedTask={taskToEdit}
            isReadOnly={isReadOnly}
            onTaskUpdated={(updatedTaskData, taskId) => {
              console.log('Task updated, updating local state:', updatedTaskData, 'for task ID:', taskId);
              // Update the local state to reflect the changes without needing to refetch from Firestore
              setTasks(prevTasks => {
                return prevTasks.map(task => {
                  if (task.id === taskId || task.assID === taskId) {
                    // Create a new task object with updated values
                    const updatedTask = { ...task };

                    // Update checkDetail properties
                    if (updatedTaskData.checkDetail) {
                      updatedTask.checkDetail = {
                        ...updatedTask.checkDetail,
                        active: updatedTaskData.checkDetail.active !== undefined ? updatedTaskData.checkDetail.active : updatedTask.checkDetail.active,
                        assignedTo: updatedTaskData.checkDetail.assignedTo || updatedTask.checkDetail.assignedTo,
                        check: updatedTaskData.checkDetail.check || updatedTask.checkDetail.check,
                        evidence: updatedTaskData.checkDetail.evidence || updatedTask.checkDetail.evidence,
                        meetingNeeded: updatedTaskData.checkDetail.meetingNeeded || updatedTask.checkDetail.meetingNeeded,
                        framework: updatedTaskData.checkDetail.framework || updatedTask.checkDetail.framework
                      };
                    }

                    // Update checkFrequency properties
                    if (updatedTaskData.checkFrequency) {
                      updatedTask.checkFrequency = {
                        ...updatedTask.checkFrequency,
                        checkDays: updatedTaskData.checkFrequency.checkDays || updatedTask.checkFrequency.checkDays,
                        reminderDays: updatedTaskData.checkFrequency.reminderDays || updatedTask.checkFrequency.reminderDays,
                        nextCheck: updatedTaskData.checkFrequency.nextCheck || updatedTask.checkFrequency.nextCheck
                      };
                    }

                    // Update checkHistory properties - this is the history of checks
                    if (updatedTaskData.checkHistory) {
                      updatedTask.checkHistory = {
                        ...updatedTask.checkHistory,
                        ...updatedTaskData.checkHistory
                      };
                    }

                    // Update rawData
                    if (updatedTask.rawData) {
                      updatedTask.rawData = {
                        ...updatedTask.rawData,
                        ...updatedTaskData
                      };
                    }

                    return updatedTask;
                  }
                  return task;
                });
              });
              // Reset the taskToEdit state
              setTaskToEdit(null);
            }}
            onTaskDeleted={handleTaskDeleted}
          />
          {/* PerformTaskDialog - Only render in non-read-only mode */}
          {!isReadOnly && (
            <PerformTaskDialog
              open={isPerformDialogOpen}
              onOpenChange={setIsPerformDialogOpen}
              onTaskPerformed={loadTasks} // Refresh task list after performing a task
              selectedEvent={taskToEdit ? {
                title: taskToEdit.checkDetail?.check,
                description: taskToEdit.checkDetail?.check,
                date: taskToEdit.checkFrequency?.nextCheck,
                // Use assID as the componentId since that's what PerformTaskDialog expects
                // This is the key in the Firestore document
                componentId: taskToEdit.assID,
                id: taskToEdit.assID, // Ensure we pass the Firestore task ID
                specialType: taskToEdit.specialType, // Pass the special type to the dialog
                rawData: taskToEdit
              } : selectedTasks.length > 0 ? {
                title: `${selectedTasks.length} tasks selected`,
                description: 'Multiple tasks selected for bulk action',
                date: new Date().toISOString(),
              } : undefined}
            />
          )}
          <TaskHistoryDialog
            isOpen={isHistoryDialogOpen}
            onClose={() => setIsHistoryDialogOpen(false)}
            editHistory={historyTask?.history || {}}
            performHistory={(() => {
              const checkHistory = historyTask?.checkHistory || {};
              // Filter out any string values from checkHistory
              const filteredHistory = Object.entries(checkHistory).reduce((acc, [key, value]) => {
                if (value && typeof value === 'object' && !Array.isArray(value)) {
                  acc[key] = value as any;
                }
                return acc;
              }, {} as Record<string, any>);
              return filteredHistory;
            })()}
            getUserName={getUserFullName}
          />

          {/* Responsive Task List: DataTable for desktop, Cards for mobile */}
          <div className="task-list">
            <div className="hidden md:block">
              <div className="w-full overflow-x-auto">
                <DataTable
                  columns={getTaskColumns(
                    helpContent,
                    {
                    onEdit: (row) => {
                      if (isReadOnly || row.isChecklistTask) return; // Disable edit when read-only or for checklist-based
                      setTaskToEdit(row);
                      setIsEditDialogOpen(true);
                    },
                    onPerform: async (row) => {
                      if (row.isChecklistTask) {
                        // In read-only mode, disable performing actions for checklists
                        if (isReadOnly) return;

                        const raw = row.rawData || {};
                        // Open dialog for checklist-based tasks
                        let checklistId = raw.id;
                        let checklist: any = null;
                        let isInstance = raw.templateId && raw.id && raw.id !== raw.templateId;
                        try {
                          const { checklistService } = await import('@/app/pages/checklists/services/checklist-service');
                          const userId = currentUser?.id || '';
                          if (isInstance) {
                            // Already an instance, fetch checklist
                            checklist = await checklistService.getChecklist(checklistId, activeTenancy.id);
                          } else {
                            // Create new instance from template
                            checklist = await checklistService.createChecklistFromTemplate(row.id, activeTenancy.id, userId);
                          }
                          setChecklistDialogChecklist(checklist);
                          setChecklistDialogOpen(true);
                        } catch (error) {
                          setError('Failed to start checklist completion.');
                        }
                      } else {
                        // In read-only mode, open the EditTaskDialog with isReadOnly=true
                        // In regular mode, open the PerformTaskDialog
                        if (isReadOnly) {
                          setTaskToEdit(row);
                          setIsEditDialogOpen(true);
                        } else {
                          setTaskToEdit(row);
                          setIsPerformDialogOpen(true);
                        }
                      }
                    },
                    onDownload: (row) => {/* TODO: handle download */ },
                    onHistory: (row) => {
                      setHistoryTask(row);
                      setIsHistoryDialogOpen(true);
                    },
                    onRestore: (row) => {
                      handleTaskRestore(row.id || row.assID || '');
                    }
                  },
                    isReadOnly,
                    showDeletedTasks
                  )}
                  data={sortedTasks}
                  pageSize={10}
                  columnDisplayConfig={assuranceTasksColumnConfig}
                />
              </div>
            </div>
            <div className="block md:hidden">
              {/* Mobile Cards */}
              {sortedTasks.map(task => (
                <div
                  className="mobile-card cursor-pointer hover:shadow-lg transition-shadow"
                  key={task.id}
                  style={{
                    backgroundColor: theme.isDark ? '#18181b' : 'white',
                    color: theme.isDark ? '#f3f4f6' : '#18181b',
                    border: `1px solid ${theme.isDark ? '#27272a' : '#e5e7eb'}`,
                    boxShadow: theme.isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                  onClick={() => {
                    // DED-921: Open the task in the edit dialog instead of navigating to a non-existent route
                    setTaskToEdit(task);
                    setIsEditDialogOpen(true);
                  }}
                  title="View Task"
                >
                  <div className="mobile-card-header flex justify-between items-center">
                    <span className="mobile-card-id">Task: {task.description}</span>
                    <button
                      className="ml-2 p-1 rounded hover:bg-primary/10"
                      onClick={e => { 
                        e.stopPropagation(); 
                        // DED-921: Open the task in the edit dialog
                        setTaskToEdit(task);
                        setIsEditDialogOpen(true);
                      }}
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mobile-card-field"><strong>Framework:</strong>
                    {Object.values(task.checkDetail.framework || {}).map((framework, index) => {
                      const frame = framework as { frameName: string };
                      return (
                        <Badge key={index} variant={getDocTypeVariant(frame.frameName) as "default" | "secondary" | "destructive" | "outline"} className="mr-2">
                          {frame.frameName}
                        </Badge>
                      );
                    })}
                  </div>
                  <div className="mobile-card-field"><strong>Owner:</strong> {getUserFullName(task.checkDetail.assignedTo) || 'Unassigned'}</div>
                  <div className="mobile-card-field"><strong>Status:</strong>
                    <Badge variant={getStatusVariant(task.checkDetail.active) as "default" | "secondary" | "destructive" | "outline"}>
                      <span className="inline-block w-2 h-2 rounded-full mr-1 bg-current opacity-70"></span>
                      {String(task.checkDetail.active) === 'true' ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="mobile-card-field"><strong>Review Date:</strong> {task.checkFrequency?.nextCheck ? renderDate(task.checkFrequency.nextCheck) : '-'}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      </div>
      {/* <TourAlertDialog isOpen={openTour} setIsOpen={setOpenTour} /> */}
      {/* Add Assurance Task Modal - Only render when not in read-only mode */}
      {!isReadOnly && (
        <AddAssuranceTaskModal
          open={addTaskModalOpen}
          onOpenChange={setAddTaskModalOpen}
          onTaskAdded={() => {
            // Reload tasks after adding
            (async () => {
              setLoading(true);
              try {
                await loadTasks();
              } catch (err) {
                setError('Failed to load assurance tasks. Please try again.');
              } finally {
                setLoading(false);
              }
            })();
          }}
        />
      )}
      <>
        {/* ...existing AssuranceLibrary JSX... */}
        <ChecklistCompleteDialog
          open={checklistDialogOpen}
          onOpenChange={open => {
            setChecklistDialogOpen(open);
            if (!open) setChecklistDialogChecklist(null);
          }}
          checklist={checklistDialogChecklist}
          onSubmit={handleChecklistDialogSubmit}
        />
      </>
    </div>

  );
}

export default AssuranceLibrary;
